import React from "react";
import { useNavigate } from "react-router-dom";
import { canUseAnalysis, canUseLineMap } from "../utils/analysisAccess";
import {
  Home,
  Bus,
  Archive,
  MessageSquare,
  Trophy,
  Shield,
  Lock,
  LayoutDashboard,
  User,
  Star,
  LogIn,
  UserPlus,
  LogOut,
  ChevronRight,
  Route as RouteIcon,
  GitCommitVertical as LineMapIcon,
} from "lucide-react";

export default function HamburgerMenu({
  menuOpen,
  setMenuOpen,
  currentUser,
  setCurrentUser,
}) {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("token");
    setCurrentUser(null);
    setMenuOpen(false);
    navigate("/login");
  };

  const go = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  return (
    <>
      {/* ハンバーガーアイコン */}
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        style={{
          position: "fixed",
          top: "14px",
          left: "14px",
          zIndex: 1100,
          width: "40px",
          height: "40px",
          borderRadius: "10px",
          background: menuOpen ? "rgba(255,255,255,0.15)" : "rgba(4,120,87,0.1)",
          border: "none",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: "5px",
          padding: "8px",
          transition: "background 0.3s ease",
        }}
        aria-label="メニュー"
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              display: "block",
              width: "18px",
              height: "2px",
              backgroundColor: menuOpen ? "#fff" : "#047857",
              borderRadius: "2px",
              transition: "all 0.3s ease",
              transformOrigin: "center",
              transform:
                menuOpen && i === 0 ? "translateY(7px) rotate(45deg)"
                : menuOpen && i === 2 ? "translateY(-7px) rotate(-45deg)"
                : "none",
              opacity: menuOpen && i === 1 ? 0 : 1,
            }}
          />
        ))}
      </button>

      {/* オーバーレイ */}
      <div
        onClick={() => setMenuOpen(false)}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(4px)",
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? "auto" : "none",
          transition: "opacity 0.35s ease",
          zIndex: 1000,
        }}
      />

      {/* スライドパネル */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "280px",
          height: "100dvh",
          background: "#fff",
          boxShadow: "4px 0 24px rgba(0,0,0,0.12)",
          transform: menuOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.35s cubic-bezier(0.4,0,0.2,1)",
          zIndex: 1050,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        {/* ヘッダー */}
        <div
          style={{
            background: "linear-gradient(135deg, #047857, #10b981)",
            padding: "48px 20px 20px",
            flexShrink: 0,
          }}
        >
          {currentUser ? (
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <User size={22} color="#fff" />
              </div>
              <div>
                <p style={{ color: "#fff", fontWeight: "700", fontSize: "0.95rem", margin: 0 }}>
                  {currentUser.name}
                </p>
                <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", margin: "2px 0 0" }}>
                  ログイン中
                </p>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Bus size={24} color="#fff" />
              <p style={{ color: "#fff", fontWeight: "700", fontSize: "1rem", margin: 0 }}>
                西鉄バスWatch!
              </p>
            </div>
          )}
        </div>

        {/* メニュー項目 */}
        <div style={{ flex: 1, padding: "8px 0" }}>
          <MenuItem icon={<Home size={18} />}        label="トップページ"     onClick={() => go("/")} />
          <MenuItem icon={<Bus size={18} />}          label="営業所一覧"       onClick={() => go("/depots")} />
          <MenuItem icon={<Archive size={18} />}      label="アーカイブ"       onClick={() => go("/archive")} />
          <MenuItem icon={<MessageSquare size={18} />} label="情報提供BBS"    onClick={() => go("/bbs")} />
          <MenuItem icon={<Trophy size={18} />}       label="投稿貢献度"       onClick={() => go("/contribution")} />
          <MenuItem icon={<Shield size={18} />}       label="投稿ルール"         onClick={() => go("/rule")} />
          <MenuItem icon={<Lock size={18} />}         label="プライバシーポリシー" onClick={() => go("/privacy")} />

          <Divider />

          {currentUser ? (
            <>
              <MenuItem icon={<User size={18} />}   label="マイページ"     onClick={() => go("/mypage")} />
              <MenuItem icon={<Star size={18} />}   label="お気に入り車両" onClick={() => go("/favorites")} />
              {canUseAnalysis(currentUser) && (
                <MenuItem icon={<RouteIcon size={18} />} label="運用解析" onClick={() => go("/analysis")} />
              )}
              {/* 路線図 View（260915追加）。2026-09-23 にログインユーザーへ公開。
                  公開範囲は LINE_MAP_ACCESS。変えるときは backend/routes/api.php の
                  line-maps も一緒に（ここだけ変えても 403） */}
              {canUseLineMap(currentUser) && (
                <MenuItem icon={<LineMapIcon size={18} />} label="路線図View" onClick={() => go("/analysis/line-map")} />
              )}
              {currentUser.user_role_id === 1 && (
                <MenuItem icon={<LayoutDashboard size={18} />} label="管理者画面" onClick={() => go("/admin")} admin />
              )}
              <MenuItem icon={<LogOut size={18} />} label="ログアウト"  onClick={handleLogout} danger />
            </>
          ) : (
            <>
              <MenuItem icon={<LogIn size={18} />}    label="ログイン"   onClick={() => go("/login")} />
              <MenuItem icon={<UserPlus size={18} />} label="新規登録"   onClick={() => go("/register")} />
            </>
          )}
        </div>
      </nav>
    </>
  );
}

function MenuItem({ icon, label, onClick, danger = false, admin = false }) {
  const [hovered, setHovered] = React.useState(false);

  const bgColor = () => {
    if (!hovered) return "transparent";
    if (danger) return "#fff1f2";
    if (admin) return "#faf5ff";
    return "#f0fdf4";
  };

  const iconColor = danger ? "#dc2626" : admin ? "#7c3aed" : "#047857";
  const textColor = danger ? "#dc2626" : admin ? "#6d28d9" : "#1f2937";

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        width: "100%",
        padding: "12px 20px",
        background: bgColor(),
        border: "none",
        cursor: "pointer",
        color: textColor,
        fontSize: "0.9rem",
        fontWeight: admin ? "600" : "500",
        textAlign: "left",
        transition: "background 0.15s ease",
      }}
    >
      <span style={{ color: iconColor, flexShrink: 0 }}>
        {icon}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
      <ChevronRight size={14} style={{ color: "#9ca3af" }} />
    </button>
  );
}

function Divider() {
  return (
    <div
      style={{
        height: "1px",
        background: "#f3f4f6",
        margin: "6px 16px",
      }}
    />
  );
}
